import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../table";
import { Card, CardContent } from "../card";
import { Checkbox } from "../checkbox";
import { cn } from "../utils";
import { DataPager } from "./data-pager";
import { capitalizeFirst } from "../format";
import { EmptyTableRow } from "./empty-table-row";
import { EmptyState } from "./empty-state";
import { SectionHeader } from "./list-card";
import { useNavigateOrPeek } from "./object-link";
import type { DataTableColumn, DataTableProps } from "./data-table";

type SortDir = "asc" | "desc";

/**
 * The client-side table engine behind `DataTable`'s interactive modes — sort,
 * pagination, j/k keyboard nav, and row selection. This is an INTERNAL module:
 * consumers always import `DataTable`, which renders this only when an
 * interactive prop is present. It absorbs the former `SortableTable` +
 * `MultiSelectTable` (one engine, one column type — `DataTableColumn`).
 *
 * It lives in its own `"use client"` file so `DataTable` itself stays a shared
 * (server-renderable) component: a server page can pass `render` functions to a
 * static `DataTable`, while interactive tables — which are always rendered from
 * client components anyway — get this engine.
 */
export function DataTableInteractive<R>({
  rows,
  columns,
  rowKey,
  rowClassName,
  rowId,
  onRowClick,
  rowActionId,
  rowAttributes,
  rowHref,
  empty = "No results.",
  emptyState,
  hideHeader = false,
  strongHeader = false,
  wrapInCard = true,
  title,
  subtitle,
  count,
  headerAction,
  footer,
  toolbar,
  summary,
  className,
  tableClassName,
  initialSort,
  pageSize,
  alwaysShowPager,
  keyboardNav,
  selectable,
  renderBulkBar,
  footerRow,
}: DataTableProps<R>) {
  /**
   * Follow a `rowHref`. A row pointing at a canonical object page (`/o/<uid>`,
   * the one shape `objectHref()` produces) opens the object PEEK in place
   * instead of navigating — Matt, 2026-07-20: clicking a goal, a customer, an
   * issue should show the modal first, with the page one Expand away. Every
   * other href navigates exactly as before. Intercepting here rather than at
   * each call site means the ~20 tables already passing `objectHref` get the
   * behavior without churn, and a new one can't forget it.
   */
  const go = useNavigateOrPeek();

  const visibleColumns = React.useMemo(
    () => columns.filter((c) => !c.hidden),
    [columns],
  );

  const [sortKey, setSortKey] = React.useState<string | null>(
    initialSort?.key ?? null,
  );
  const [sortDir, setSortDir] = React.useState<SortDir>(
    initialSort?.dir ?? "asc",
  );
  const [page, setPage] = React.useState(1);
  const [focusedIdx, setFocusedIdx] = React.useState<number | null>(null);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const rowRefs = React.useRef<(HTMLTableRowElement | null)[]>([]);

  // If the active sort key is hidden/removed, fall back to the initial sort.
  React.useEffect(() => {
    if (sortKey && !visibleColumns.some((c) => c.key === sortKey)) {
      setSortKey(initialSort?.key ?? null);
      setSortDir(initialSort?.dir ?? "asc");
    }
  }, [visibleColumns, sortKey, initialSort?.key, initialSort?.dir]);

  const sorted = React.useMemo(() => {
    const col = visibleColumns.find((c) => c.key === sortKey);
    if (!col || !col.sort) return rows;
    const cmp = col.sort;
    const dir = sortDir === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => cmp(a, b) * dir);
  }, [rows, visibleColumns, sortKey, sortDir]);

  const totalPages = pageSize ? Math.ceil(sorted.length / pageSize) : 1;
  const paginated = pageSize
    ? sorted.slice((page - 1) * pageSize, page * pageSize)
    : sorted;

  React.useEffect(() => {
    if (page > totalPages && totalPages > 0) setPage(1);
  }, [page, totalPages]);

  // Prune selections that no longer exist (filter/search shrank the rows).
  const liveKeys = React.useMemo(
    () => new Set(rows.map((r, i) => String(rowKey(r, i)))),
    [rows, rowKey],
  );
  React.useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((k) => liveKeys.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [liveKeys]);

  function toggleSort(col: DataTableColumn<R>) {
    if (col.sortable === false || !col.sort) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(col.key);
      setSortDir(col.defaultDesc ? "desc" : "asc");
    }
    setPage(1);
    setFocusedIdx(null);
  }

  // j/k keyboard navigation (opt-in)
  React.useEffect(() => {
    if (!keyboardNav) return;
    function handler(e: KeyboardEvent) {
      const el = document.activeElement as HTMLElement | null;
      const tag = el?.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea" || el?.isContentEditable) return;
      if (e.key === "j") {
        e.preventDefault();
        setFocusedIdx((i) => {
          const next = i === null ? 0 : Math.min(i + 1, paginated.length - 1);
          rowRefs.current[next]?.scrollIntoView({ block: "nearest" });
          return next;
        });
      } else if (e.key === "k") {
        e.preventDefault();
        setFocusedIdx((i) => {
          const next = i === null ? 0 : Math.max(i - 1, 0);
          rowRefs.current[next]?.scrollIntoView({ block: "nearest" });
          return next;
        });
      } else if (e.key === "Enter" && focusedIdx !== null && rowHref) {
        const row = paginated[focusedIdx];
        if (row) go(rowHref(row));
      } else if (e.key === "Escape" && focusedIdx !== null) {
        setFocusedIdx(null);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [keyboardNav, focusedIdx, paginated, rowHref, go]);

  const selectedRows = rows.filter((r, i) => selected.has(String(rowKey(r, i))));
  const allSelected =
    paginated.length > 0 &&
    paginated.every((r, i) => selected.has(String(rowKey(r, i))));
  const clearSelection = () => setSelected(new Set());
  const toggleOne = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const toggleAllVisible = () =>
    setSelected(
      allSelected
        ? new Set()
        : new Set(paginated.map((r, i) => String(rowKey(r, i)))),
    );

  const colCount = visibleColumns.length + (selectable ? 1 : 0);

  const headerRow = hideHeader ? null : (
    <TableHeader>
      <TableRow>
        {selectable ? (
          // Center the checkbox in its column. The atom zeroes the right pad on
          // a checkbox cell (:has(checkbox)→pr-0), which pins the box; the
          // compound [&:first-child:has(...)] variant restores a symmetric px-2
          // without !important, and the flex wrapper centers it (making the
          // checkbox a non-direct child also skips the atom's translate-y nudge
          // so header + body checkboxes stay aligned).
          <TableHead className="w-10 [&:first-child:has([role=checkbox])]:px-2">
            <div className="flex items-center justify-center">
              <Checkbox
                aria-label="Select all"
                checked={allSelected}
                onCheckedChange={toggleAllVisible}
              />
            </div>
          </TableHead>
        ) : null}
        {visibleColumns.map((col) => {
          const isSortable = col.sortable !== false && !!col.sort;
          const active = sortKey === col.key;
          const alignClass =
            col.align === "right"
              ? "text-right"
              : col.align === "center"
                ? "text-center"
                : undefined;
          const label =
            typeof col.header === "string"
              ? capitalizeFirst(col.header)
              : col.header;
          return (
            <TableHead
              key={col.key}
              className={cn(col.width, alignClass, col.headerClassName)}
            >
              {isSortable ? (
                <button
                  type="button"
                  onClick={() => toggleSort(col)}
                  className={cn(
                    "w-full whitespace-nowrap transition-opacity hover:opacity-60",
                    strongHeader
                      ? "font-medium text-foreground"
                      : "text-muted-foreground",
                    col.align === "right"
                      ? "text-right"
                      : col.align === "center"
                        ? "text-center"
                        : "text-left",
                  )}
                >
                  {label} {active ? (sortDir === "desc" ? "↓" : "↑") : ""}
                </button>
              ) : strongHeader ? (
                <span className="font-medium text-foreground">{label}</span>
              ) : (
                label
              )}
            </TableHead>
          );
        })}
      </TableRow>
    </TableHeader>
  );

  const tableEl = (
    <Table className={tableClassName}>
      {headerRow}
      <TableBody>
        {paginated.length === 0 ? (
          emptyState ? (
            <EmptyState variant="row" colSpan={colCount} {...emptyState} />
          ) : (
            <EmptyTableRow colSpan={colCount}>{empty}</EmptyTableRow>
          )
        ) : (
          paginated.map((row, i) => {
            const key = String(rowKey(row, i));
            const focused = i === focusedIdx;
            const clickable = !!onRowClick || !!rowHref;
            return (
              <TableRow
                key={key}
                id={rowId?.(row, i)}
                data-action-id={rowActionId}
                {...rowAttributes?.(row, i)}
                ref={(el) => {
                  rowRefs.current[i] = el;
                }}
                className={cn(
                  clickable &&
                    "cursor-pointer transition-colors hover:bg-secondary",
                  focused && "ring-1 ring-primary ring-inset",
                  rowClassName?.(row, i),
                )}
                onClick={
                  clickable
                    ? (e) => {
                        // Don't double-navigate when the click hits an inner
                        // link/button/checkbox (cells carry their own).
                        if (
                          (e.target as HTMLElement).closest("a,button,input")
                        ) {
                          return;
                        }
                        if (onRowClick) onRowClick(row);
                        else if (rowHref) go(rowHref(row));
                      }
                    : undefined
                }
              >
                {selectable ? (
                  <TableCell data-label="Select" className="w-10 [&:first-child:has([role=checkbox])]:px-2">
                    <div
                      className="flex items-center justify-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        aria-label="Select row"
                        checked={selected.has(key)}
                        onCheckedChange={() => toggleOne(key)}
                      />
                    </div>
                  </TableCell>
                ) : null}
                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    data-label={typeof col.header === "string" ? capitalizeFirst(col.header) : capitalizeFirst(col.key)}
                    className={cn(
                      col.align === "right" && "text-right tabular-nums",
                      col.align === "center" && "text-center",
                      col.cellClassName,
                    )}
                  >
                    {col.render(row, i)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })
        )}
        {footerRow && paginated.length > 0 ? footerRow(paginated) : null}
      </TableBody>
    </Table>
  );

  // One shared in-card pager (also used by the server-paginated DatabaseGrid);
  // it self-hides when there's a single page, so a one-page table shows no
  // footer at all.
  const pager =
    pageSize != null ? (
      <DataPager
        page={page}
        totalPages={totalPages}
        total={sorted.length}
        wrapped={wrapInCard}
        alwaysShow={alwaysShowPager}
        onPageChange={(p) => {
          setPage(p);
          setFocusedIdx(null);
        }}
      />
    ) : null;

  const bulkBar =
    selectable && renderBulkBar && selectedRows.length > 0 ? (
      <div
        className={cn(
          "flex flex-wrap items-center gap-2 border-b border-border py-2.5",
          wrapInCard ? "px-4" : "mb-3",
        )}
      >
        <span className="type-body text-muted-foreground">
          {selectedRows.length} selected
        </span>
        {renderBulkBar(selectedRows, clearSelection)}
      </div>
    ) : null;

  const toolbarBand =
    toolbar != null ? (
      <div className={cn("border-b border-border py-2.5", wrapInCard ? "px-4" : "mb-3")}>
        {toolbar}
      </div>
    ) : null;

  const body = (
    <>
      {toolbarBand}
      {bulkBar}
      {tableEl}
      {footer != null ? (
        <div className="border-t border-border/40 px-4 py-2.5 type-body text-muted-foreground">
          {footer}
        </div>
      ) : null}
      {pager}
    </>
  );

  if (!wrapInCard) {
    return <div className={className}>{body}</div>;
  }

  return (
    <Card
      className={cn(
        "gap-0 py-0 overflow-hidden border border-border",
        className,
      )}
    >
      <CardContent className="px-0">
        {title != null ? (
          <SectionHeader
            title={title}
            subtitle={subtitle}
            count={count}
            action={headerAction}
          />
        ) : null}
        {summary != null ? summary : null}
        {body}
      </CardContent>
    </Card>
  );
}
